import { z } from "zod";
import { agentCardIconSchema, agentCardStyleColorSchema, agentCardSectionSchema } from "./cards.js";
import { openCloudUIMessageSchema } from "./ui-messages.js";
import { agentAttachmentsSchema } from "./attachments.js";
import { agentMessagePresentationSchema, agentMessageRoleSchema, } from "./messages.js";
import { agentRunPhaseSchema, agentRunStatusSchema, agentRunTriggerSchema, } from "./runs.js";
import { agentTaskListSchema } from "./task-lists.js";
import { agentSkillCatalogIdentitySchema } from "./skills.js";
import { appIdSchema, isoTimestampSchema, runIdSchema, threadIdSchema, } from "./identifiers.js";
export const PLATFORM_CONVERSATION_REVIEW_DEFAULT_MESSAGE_LIMIT = 200;
export const PLATFORM_CONVERSATION_REVIEW_MAX_MESSAGE_LIMIT = 1_000;
export const PLATFORM_CONVERSATION_REVIEW_RUN_LIMIT = 500;
export const PLATFORM_CONVERSATION_REVIEW_MESSAGE_CONTENT_LIMIT = 50_000;
export const PLATFORM_CONVERSATION_REVIEW_MESSAGE_TOTAL_CONTENT_LIMIT = 2_000_000;
export const PLATFORM_CONVERSATION_REVIEW_TOOL_CONTENT_LIMIT = 16_000;
export const platformConversationReviewThreadSchema = z
    .object({
    id: threadIdSchema,
    appId: appIdSchema,
    title: z.string().trim().min(1).max(500),
    status: z.enum(["active", "archived"]),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
})
    .strict();
const platformConversationReviewMessageSchema = z
    .object({
    schemaVersion: z.literal(1),
    id: z.uuid(),
    threadId: threadIdSchema,
    appId: appIdSchema,
    runId: runIdSchema.nullable(),
    role: agentMessageRoleSchema,
    presentation: agentMessagePresentationSchema.optional(),
    content: z
        .string()
        .max(PLATFORM_CONVERSATION_REVIEW_MESSAGE_CONTENT_LIMIT),
    attachments: agentAttachmentsSchema.optional(),
    contentTruncated: z.boolean(),
    createdAt: isoTimestampSchema,
    sequence: z.number().int().nonnegative(),
})
    .strict();
const platformConversationReviewBlockerSchema = z
    .object({
    kind: z.enum([
        "rate_limit",
        "usage_limit",
        "authentication",
        "provider_unavailable",
    ]),
    retryAt: isoTimestampSchema.nullable(),
    requiredAction: z.enum(["wait", "reconnect_provider"]),
})
    .strict();
const platformConversationReviewQueueSchema = z
    .object({
    position: z.number().int().nonnegative(),
    reason: z.enum([
        "ai_capacity",
        "execution_user_capacity",
        "execution_instance_capacity",
        "app_active",
        "app_blocked",
        "user_capacity",
        "scheduler",
    ]),
    activeApps: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
    retryAt: isoTimestampSchema.nullable(),
})
    .strict();
export const platformConversationReviewRunSchema = z
    .object({
    id: runIdSchema,
    threadId: threadIdSchema,
    parentRunId: runIdSchema.nullable(),
    trigger: agentRunTriggerSchema,
    status: agentRunStatusSchema,
    phase: agentRunPhaseSchema,
    attempt: z.number().int().nonnegative(),
    checkpointVersion: z.number().int().nonnegative(),
    terminalErrorClass: z.string().trim().min(1).max(500).nullable(),
    terminalRetryable: z.boolean().nullable(),
    childPurpose: z.string().trim().min(1).max(500).nullable(),
    childOutcome: z.enum(["succeeded", "failed", "cancelled"]).nullable(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
    startedAt: isoTimestampSchema.nullable(),
    finishedAt: isoTimestampSchema.nullable(),
    blocker: platformConversationReviewBlockerSchema.nullable(),
    lastMeaningfulEventAt: isoTimestampSchema.nullable(),
    queue: platformConversationReviewQueueSchema.nullable(),
    diagnostics: z.object({
        selectedHarnessVersion: z.string().max(200).nullable(),
        observedHarnessVersion: z.string().max(200).nullable(),
        observedHarnessDigest: z.string().max(200).nullable(),
        skillCatalog: agentSkillCatalogIdentitySchema.nullable(),
    }).strict().optional(),
})
    .strict();
const platformConversationReviewQuestionSchema = z
    .object({
    questionId: z.uuid(),
    runId: runIdSchema,
    question: z.string().min(1).max(200_000),
    reason: z.enum(["material_decision", "new_authority", "policy"]),
    context: z.string().max(20_000).nullable(),
    options: z
        .array(z
        .object({
        id: z.string().trim().min(1).max(100),
        label: z.string().trim().min(1).max(500),
        detail: z.string().trim().min(1).max(2_000),
        response: z.string().trim().min(1).max(20_000),
        tone: z.enum(["default", "primary", "destructive", "decline"]),
    })
        .strict())
        .max(10),
    occurredAt: isoTimestampSchema,
})
    .strict();
const platformConversationReviewActivitySchema = z
    .object({
    id: z.string().trim().min(1).max(500),
    runId: runIdSchema,
    kind: z.enum(["progress", "check", "blocked", "release", "terminal"]),
    label: z.string().trim().min(1).max(1_000),
    detail: z.string().trim().min(1).max(5_000).nullable(),
    status: z.enum(["active", "complete", "failed"]),
    occurredAt: isoTimestampSchema,
})
    .strict();
const platformConversationReviewReviewEvidenceSchema = z
    .object({
    area: z.enum(["build", "behavior", "experience"]),
    label: z.string().trim().min(1).max(500),
    status: z.enum(["passed", "needs_work", "not_needed"]),
    detail: z.string().trim().min(1).max(2_000),
})
    .strict();
const platformConversationReviewGateEvidenceSchema = z
    .object({
    id: z.string().trim().min(1).max(500),
    label: z.string().trim().min(1).max(500),
    status: z.enum(["passed", "failed", "not_applicable", "not_tested"]),
    detail: z.string().trim().min(1).max(2_000),
})
    .strict();
const platformConversationReviewReleaseSchema = z
    .object({
    deploymentId: z.uuid().nullable(),
    status: z.enum(["passed", "failed"]),
    reviews: z.array(platformConversationReviewReviewEvidenceSchema).max(20),
    gates: z.array(platformConversationReviewGateEvidenceSchema).max(100),
})
    .strict();
export const platformConversationReviewReleaseDetailsSchema = z.object({
    id: z.uuid(), appId: appIdSchema, version: z.string().max(200), state: z.string().max(100),
    current: z.boolean(), canRevert: z.literal(false), automatic: z.boolean(),
    kind: z.enum(["release", "revert"]), rollbackOfDeploymentId: z.uuid().nullable(),
    createdAt: isoTimestampSchema, activatedAt: isoTimestampSchema.nullable(),
    summary: z.string().max(10_000), capabilities: z.array(z.string().max(500)).max(20),
    reviews: z.array(platformConversationReviewReviewEvidenceSchema).max(20),
    gates: z.array(platformConversationReviewGateEvidenceSchema).max(100),
}).strict();
const platformConversationReviewOutcomeSchema = z
    .object({
    runId: runIdSchema,
    status: z.enum(["released", "completed", "failed", "cancelled"]),
    summary: z.string().trim().min(1).max(10_000),
    occurredAt: isoTimestampSchema,
    release: platformConversationReviewReleaseSchema.nullable(),
})
    .strict();
function readOnlySection(section) {
    return section.type !== "actions" && section.type !== "choices" &&
        (section.type !== "disclosure" || section.sections.every(readOnlySection));
}
export const platformConversationReviewCardSchema = z
    .object({
    runId: runIdSchema,
    occurredAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
    card: z
        .object({
        id: z.string().trim().min(1).max(100),
        type: z.enum([
            "work_card",
            "question_card",
            "permission_card",
            "action_required_card",
            "release_card",
            "completion_card",
            "failure_card",
            "stopped_card",
            "live_problem_card",
        ]),
        revision: z.number().int().positive(),
        state: z.enum(["live", "settled"]),
        title: z.string().trim().min(1).max(500),
        subtitle: z.string().trim().min(1).max(500),
        icon: agentCardIconSchema.optional(),
        styleColor: agentCardStyleColorSchema.optional(),
        sections: z.array(agentCardSectionSchema.refine(readOnlySection, "Owner actions are not part of review")).max(12).optional(),
    })
        .strict(),
})
    .strict();
const platformConversationReviewTaskListSchema = z
    .object({
    runId: runIdSchema,
    occurredAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
    taskList: agentTaskListSchema,
})
    .strict();
const platformConversationReviewToolCallSchema = z
    .object({
    id: z.string().trim().min(1).max(500),
    runId: runIdSchema,
    tool: z.string().trim().min(1).max(500),
    kind: z.enum(["command", "tool"]),
    summary: z.string().trim().min(1).max(2_000),
    input: z
        .string()
        .max(PLATFORM_CONVERSATION_REVIEW_TOOL_CONTENT_LIMIT)
        .nullable(),
    inputTruncated: z.boolean(),
    output: z
        .string()
        .max(PLATFORM_CONVERSATION_REVIEW_TOOL_CONTENT_LIMIT)
        .nullable(),
    outputTruncated: z.boolean(),
    operationIds: z.array(z.uuid()).max(32).optional(),
    status: z.enum(["running", "succeeded", "failed", "cancelled"]),
    errorClass: z.string().trim().min(1).max(500).nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    startedAt: isoTimestampSchema,
    finishedAt: isoTimestampSchema.nullable(),
    sequence: z.number().int().nonnegative(),
})
    .strict();
const reviewWindowSchema = z
    .object({
    limit: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    truncated: z.boolean(),
})
    .strict();
export const platformConversationReviewSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    cursor: z.number().int().nonnegative(),
    thread: platformConversationReviewThreadSchema,
    messages: z
        .array(platformConversationReviewMessageSchema)
        .max(PLATFORM_CONVERSATION_REVIEW_MAX_MESSAGE_LIMIT),
    runs: z
        .array(platformConversationReviewRunSchema)
        .max(PLATFORM_CONVERSATION_REVIEW_RUN_LIMIT),
    question: platformConversationReviewQuestionSchema.nullable(),
    activity: z.array(platformConversationReviewActivitySchema).max(60),
    outcomes: z.array(platformConversationReviewOutcomeSchema).max(30),
    cards: z.array(platformConversationReviewCardSchema).max(30),
    taskLists: z.array(platformConversationReviewTaskListSchema).max(30),
    toolCalls: z.array(platformConversationReviewToolCallSchema).max(100),
    uiMessages: z.array(openCloudUIMessageSchema.refine(message => message.parts.every(part => part.type !== "reasoning" &&
        ((part.type !== "data-card" && part.type !== "data-release") || part.data.card.sections.every(readOnlySection))), "Review messages cannot contain protected reasoning or owner actions")).max(2_000).optional(),
})
    .strict();
export const platformConversationReviewSchema = z
    .object({
    schemaVersion: z.literal(1),
    thread: platformConversationReviewThreadSchema.nullable(),
    snapshot: platformConversationReviewSnapshotSchema.nullable(),
    working: z.boolean(),
    readOnly: z.literal(true),
    messageWindow: reviewWindowSchema,
    runWindow: reviewWindowSchema,
})
    .strict()
    .superRefine((value, context) => {
    if ((value.thread === null) !== (value.snapshot === null)) {
        context.addIssue({
            code: "custom",
            message: "Conversation review thread and snapshot must both be present",
        });
    }
    if (value.thread && value.snapshot?.thread.id !== value.thread.id) {
        context.addIssue({
            code: "custom",
            path: ["snapshot", "thread", "id"],
            message: "Conversation review snapshot targets a different thread",
        });
    }
    if (value.messageWindow.returned !== (value.snapshot?.messages.length ?? 0) ||
        value.runWindow.returned !== (value.snapshot?.runs.length ?? 0)) {
        context.addIssue({
            code: "custom",
            message: "Conversation review window counts do not match the snapshot",
        });
    }
});
//# sourceMappingURL=conversation-review.js.map